import { useCallback, useEffect, useState } from 'react';
import {
  useGetOrderDetail,
  type OrderDetail,
} from '@/api/use-get-order-detail';
import type { User } from '@/types/auth.types';

const STORAGE_KEYS = {
  LATEST_ORDER: 'ahafood.latest_order_code',
} as const;

const SEVEN_DAYS_SECONDS = 7 * 24 * 60 * 60;

// --- Storage helpers ---
function getLatestOrderCode(): string | null {
  return localStorage.getItem(STORAGE_KEYS.LATEST_ORDER);
}

function clearLatestOrderCode() {
  localStorage.removeItem(STORAGE_KEYS.LATEST_ORDER);
}

// --- Hook ---
interface UseOrderRatingParams {
  user: User | null;
  isAuthenticated: boolean;
}

interface UseOrderRatingReturn {
  pendingRatingOrder: OrderDetail | null;
  showRatingModal: boolean;
  setShowRatingModal: (show: boolean) => void;
  markOrderAsRated: () => void;
  checkPendingRating: () => void;
  checkRatingOnCityPage: () => void;
}

export function useOrderRating({
  user,
  isAuthenticated,
}: UseOrderRatingParams): UseOrderRatingReturn {
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [latestOrderCode, setLatestOrderCode] = useState<string | null>(null);
  const [pendingRatingOrder, setPendingRatingOrder] =
    useState<OrderDetail | null>(null);

  const { data: orderDetail } = useGetOrderDetail(
    user?.systemToken ?? '',
    latestOrderCode ?? '',
    {
      enabled: !!user?.systemToken && !!latestOrderCode,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  // --- Mark as rated (user đã đánh giá xong) ---
  const markOrderAsRated = useCallback(() => {
    clearLatestOrderCode();
    setLatestOrderCode(null);
    setPendingRatingOrder(null);
    setShowRatingModal(false);
  }, []);

  // --- Kiểm tra khi vào app ---
  const checkPendingRating = useCallback(() => {
    if (!isAuthenticated || !user?.systemToken) return;
    const orderCode = getLatestOrderCode();
    if (orderCode) {
      setLatestOrderCode(orderCode);
    }
  }, [isAuthenticated, user?.systemToken]);

  // --- Check khi ở CityPage (chỉ show 1 lần duy nhất) ---
  const checkRatingOnCityPage = useCallback(() => {
    if (!isAuthenticated || !user?.systemToken) return;
    const orderCode = getLatestOrderCode();
    if (!orderCode) return;

    setLatestOrderCode(orderCode);
    // ❌ KHÔNG clear ở đây nữa
  }, [isAuthenticated, user?.systemToken]);

  // --- Khi có orderDetail ---
  useEffect(() => {
    if (!orderDetail || !latestOrderCode) return;

    // 1. Chỉ xét order COMPLETED
    if (orderDetail.status !== 'COMPLETED') {
      return; // 🚫 KHÔNG clear localStorage ở đây
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeSinceCompletion = currentTime - (orderDetail.completed_time || 0);

    // 2. Nếu quá 7 ngày => clear
    if (timeSinceCompletion > SEVEN_DAYS_SECONDS) {
      console.log('clearLatestOrderCode 99');

      clearLatestOrderCode();
      setLatestOrderCode(null);
      return;
    }

    // 3. Nếu server đã có rating => clear
    if (orderDetail.rating_summary?.overall > 0) {
      console.log('clearLatestOrderCode 109');

      clearLatestOrderCode();
      setLatestOrderCode(null);
      return;
    }

    // 4. Nếu thỏa hết điều kiện => show modal & clear
    setPendingRatingOrder(orderDetail);
    setShowRatingModal(true);
    console.log('clearLatestOrderCode 118');

    clearLatestOrderCode();
  }, [orderDetail, latestOrderCode]);

  // --- Run on auth change ---
  useEffect(() => {
    checkPendingRating();
  }, [checkPendingRating]);

  return {
    pendingRatingOrder,
    showRatingModal,
    setShowRatingModal,
    markOrderAsRated,
    checkPendingRating,
    checkRatingOnCityPage,
  };
}
